
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, BookOpen, Bell, CalendarDays, Users, DollarSign, FileText, FileSpreadsheet, Settings, LinkIcon, ExternalLink, CheckCircle, Search, Database, FilePlus, ClipboardEdit } from 'lucide-react';
import { useAuth } from '@/context/auth';

const announcement = {
  title: "ประกาศ: ข้อกำหนดการใช้ชีทในการสอน",
  introduction: "เพื่อให้การใช้สื่อการเรียนการสอนเป็นไปในทิศทางเดียวกัน มีมาตรฐาน และสามารถตรวจสอบได้ ขอให้ครูทุกท่านปฏิบัติตามแนวทางต่อไปนี้อย่างเคร่งครัด",
  sections: [
    {
      title: "แนวทางการใช้ชีท (ต้องใช้ชีทของสถาบัน)",
      points: [
        "หากมีชีทของสถาบันอยู่ในคลังกลางต้องใช้ชีทของสถาบันเท่านั้น หากใช้ชีทตัวเองจะไม่ได้รับค่าชีทเพิ่ม",
        "หากมีชีทของสถาบันแต่เนื้อหาไม่ครบ ให้แจ้งส่วนกลางเพื่อแก้ไขผ่านฟอร์มนี้: https://forms.gle/dGBXpv9HxA4YTgSn6",
        "จะไม่ได้รับค่าชีทเพิ่มหากทำเองก่อนขออนุมัติ",
        "หากไม่มีชีทในคลังกลางเลย ให้ขออนุมัติจากส่วนกลางก่อนทำ หากส่วนกลางจัดหาให้ไม่ทัน ทางส่วนกลางจะอนุมัติให้ครูทำชีทเอง และครูจะได้รับค่าชีท"
      ]
    },
    {
      title: "ข้อห้ามสำคัญ",
      points: [
        "ห้ามทำชีทเองโดยไม่แจ้งส่วนกลาง",
        "ห้ามใช้ชีทที่ไม่ผ่านเกณฑ์ เช่น ไม่มีแบบฝึกหัด ถ่ายจากหนังสือ เนื้อหาไม่ครบ"
      ]
    },
    {
      title: "สิ่งที่ครูต้องดำเนินการ",
      points: [
        "ต้องส่งพัฒนาการทุกครั้งหลังการสอน พร้อมแนบลิงก์ชีทที่ใช้จริง",
        "หากได้รับการอนุมัติให้ใช้ชีทตัวเอง ต้องอัปโหลดไฟล์ลง Google Drive และแจ้งผ่านแบบฟอร์มส่งพัฒนาการ เพื่อให้สถาบันตรวจสอบและอนุมัติการจ่าย",
        "หากไม่มีการอัปโหลดไฟล์ชีท จะไม่ได้รับค่าชีท",
        "ต้องส่งเป็นไฟล์ PDF หรือ DOC เท่านั้น การส่งแค่ภาพแคปหน้าจอจะไม่ได้รับการอนุมัติ เนื่องจากไม่สามารถตรวจสอบคุณภาพตามเกณฑ์ Ideal ได้"
      ]
    },
    {
      title: "กรณีที่จะไม่จ่ายค่าชีทให้ครู",
      points: [
        "ใช้ชีทซ้ำกับของสถาบัน",
        "ชีทไม่ผ่านเกณฑ์ Ideal เช่น ไม่มีแบบฝึก 3 ระดับ, ดีไซน์ไม่ถูกต้อง, ใช้ภาษาพูด",
        "ชีทไม่ใช่ของครูเอง (ถ่ายจากหนังสือ, note บน iPad, หรือจากสถาบันอื่น)",
        "ไม่แจ้งล่วงหน้าหรือไม่ได้รับอนุมัติ",
        "ไม่ส่งพัฒนาการหรือไม่มีลิงก์ชีทประกอบการใช้งานจริง",
        "ใช้เฉพาะชีทของนักเรียน โดยครูไม่ได้จัดทำเอง"
      ]
    },
    {
      title: "ชีทที่ได้รับค่าตอบแทน",
      points: [
        "ชีทที่ได้รับค่าตอบแทนจะถือเป็นลิขสิทธิ์ของสถาบัน สามารถนำไปปรับใช้ในคลาสอื่นได้ (แต่จะได้รับค่าชีทเพียงครั้งเดียว)"
      ]
    }
  ],
  importantLinks: [
    { text: "ฟอร์มรายงานปัญหาชีท / ขอปรับปรุงเนื้อหา (แจ้งล่วงหน้าอย่างน้อย 7 วัน):", url: "https://forms.gle/dGBXpv9HxA4YTgSn6" },
    { text: "ชีทกลางหลักสูตรไทย:", url: "https://docs.google.com/spreadsheets/d/1nUsBwsn2cFyqP7DWv2gbJEAyBc5GlHmlSZ_1rpoWbWI/edit#gid=412149679" },
    { text: "ชีทกลางหลักสูตร Inter:", url: "https://docs.google.com/spreadsheets/d/1dNdzuwYa5rMDKRKTzj1nBabTcMCO9G0zWWdo7VJPYTA/edit#gid=602232550" },
    { text: "เกณฑ์ Ideal ที่ใช้ในการตรวจ:", url: "https://docs.google.com/document/d/1ik4dR2hVCtiQvif1Tt9fNLuIclYvITGIyacJv1VQfg0/edit" }
  ]
};

const LandingPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();

  // Define quick access links with an admin property to indicate admin-only access
  const quickAccessLinks = [
    { name: t('landing.links.createReport'), icon: <FilePlus className="h-6 w-6 mb-2 text-green-600" />, path: "/form", adminOnly: false },
    { name: t('landing.links.schedule'), icon: <CalendarDays className="h-6 w-6 mb-2 text-sky-600" />, path: "/teacher-schedule", adminOnly: false },
    { name: t('landing.links.students'), icon: <Users className="h-6 w-6 mb-2 text-green-500" />, path: "/admin/students", adminOnly: true },
    { name: t('landing.links.salary'), icon: <DollarSign className="h-6 w-6 mb-2 text-red-500" />, path: "/salary", adminOnly: false },
    { name: t('landing.links.worksheetCheck'), icon: <FileSpreadsheet className="h-6 w-6 mb-2 text-purple-500" />, path: "/admin/worksheet-check", adminOnly: true },
    { name: t('landing.links.worksheetManagement'), icon: <Database className="h-6 w-6 mb-2 text-indigo-500" />, path: "/admin/worksheet-management", adminOnly: true },
    { name: t('landing.links.worksheetSearch'), icon: <Search className="h-6 w-6 mb-2 text-blue-500" />, path: "/worksheet-search", adminOnly: false },
    { name: t('landing.links.reports'), icon: <FileText className="h-6 w-6 mb-2 text-yellow-500" />, path: "/teacher-reports", adminOnly: false },
    { name: t('landing.links.trials'), icon: <ClipboardEdit className="h-6 w-6 mb-2 text-orange-500" />, path: "/teacher-trials", adminOnly: false },
    { name: t('landing.links.settings'), icon: <Settings className="h-6 w-6 mb-2 text-gray-500" />, path: "/profile", adminOnly: false },
  ];

  // Filter quick access links based on user's admin status
  const filteredQuickAccessLinks = quickAccessLinks.filter(link => !link.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <main className="flex-grow container mx-auto px-0 sm:px-4 py-2 sm:py-0">
        <div className="space-y-6 md:space-y-8 p-4 md:p-8">
          {/* Quick Access Links Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-700">{t('landing.quickAccess')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {filteredQuickAccessLinks.map((link) => (
                <Button 
                  key={link.path} 
                  variant="outline" 
                  className="flex flex-col items-center justify-center h-32 text-center p-4 hover:shadow-lg transition-shadow"
                  asChild
                >
                  <Link to={link.path}>
                    {link.icon}
                    <span className="text-sm font-medium">{link.name}</span>
                  </Link>
                </Button>
              ))}
            </div>
          </section>

          {/* Latest News Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-700">{t('landing.latestNews')}</h2>
            <Card className="w-full shadow-lg">
              <CardHeader>
                <div className="flex items-center">
                  <Bell className="h-6 w-6 mr-3 text-blue-600" />
                  <CardTitle className="text-xl md:text-2xl text-blue-700">{announcement.title}</CardTitle>
                </div>
                <CardDescription className="mt-2 text-gray-600">{announcement.introduction}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {announcement.sections.map((section, index) => (
                  <div key={index}>
                    <h4 className="font-semibold text-md md:text-lg text-gray-800 mb-2 flex items-center">
                      {section.title.includes("ข้อห้าม") ? 
                          <AlertTriangle className="h-5 w-5 mr-2 text-red-500" /> : 
                          section.title.includes("ต้องดำเนินการ") ?
                          <CheckCircle className="h-5 w-5 mr-2 text-green-500" /> :
                          <BookOpen className="h-5 w-5 mr-2 text-indigo-500" /> 
                      }
                      {section.title}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-sm md:text-base text-gray-700 pl-4">
                      {section.points.map((point, pIndex) => {
                        const parts = point.split(/(https?:\/\/[^\s]+)/g);
                        return (
                          <li key={pIndex}>
                            {parts.map((part, i) => 
                              part.startsWith('http') ? (
                                <a href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center" key={i}>
                                  {part} <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                              ) : (
                                part
                              )
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
                <div>
                  <h4 className="font-semibold text-md md:text-lg text-gray-800 mb-2 mt-6 flex items-center">
                      <LinkIcon className="h-5 w-5 mr-2 text-teal-500" />
                      ลิงก์สำคัญ (ในประกาศ)
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm md:text-base text-gray-700 pl-4">
                    {announcement.importantLinks.map((link, index) => (
                      <li key={index}>
                        {link.text}{' '}
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">
                          {link.url} <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
              <CardFooter>
                <p className="text-xs text-gray-500">ประกาศ ณ วันที่ 16 พฤษภาคม 2568</p>
              </CardFooter>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
